import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import Button from 'components/common/Button';
import { FormBlock } from 'modules/student/curriculum/helpers';
import chunkBy from 'utils/chunkBy';
import { BLOCK_TYPES } from 'constant/form-builder';
import DoneAllIcon from '@material-ui/icons/DoneAll';

const useStyles = makeStyles((theme) => ({
  root: {
    'width': '100%',
    '& .MuiFormLabel-root': {
      fontSize: 18,
      fontWeight: 500,
      color: '#626C82',
    },
    '& .MuiFormGroup-root': {
      marginTop: theme.spacing(2),
    },
    '& .MuiFormHelperText-root': {
      fontSize: 14,
    },
    '& .MuiTypography-body1': {
      fontSize: 18,
      color: '#AAB5CE',
    },
    '& .MuiTableContainer-root': {
      boxShadow: 'none',
    },
    '& .MuiTableCell-head': {
      color: '#AAB5CE',
    },
    '& .radio-label': {
      fontSize: 14,
      color: theme.palette.text.primary,
    },
    '& .checkbox-label': {
      fontSize: 14,
      color: theme.palette.text.primary,
    },
  },
  stepperRoot: {
    backgroundColor: 'transparent',
    padding: 0,
    marginBottom: theme.spacing(6),
  },
  alternativeLabel: {
    flex: 0,
    padding: 0,
    marginRight: 10,
  },
  stepButton: {
    'width': 40,
    'height': 10,
    'backgroundColor': '#D2D7E3',
    'borderRadius': 12,
    'border': 0,
    'cursor': 'pointer',
    '&.active': {
      backgroundColor: '#8ED76E',
    },
  },
  footer: {
    marginTop: theme.spacing(5),
    display: 'flex',
    justifyContent: 'space-between',
  },
  input: {
    '& .MuiInputBase-input' :{
      backgroundColor: theme.palette.action.default,
      '&:focus': {
        backgroundColor: theme.palette.primary.light,
      },
    }
  }
}));

const Block = React.memo(({ block, onChange, index, classes }) => {
  return (
    <FormBlock name={`blocks.${index}`} block={block} onChange={onChange} classes={classes}/>
  );
});

const ExerciseSteps = ({
  blocks,
  onChange,
  onSave,
  onSubmit,
  status,
  hideActions,
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = React.useState(0);
  const chunks = chunkBy(blocks, (b) => b.type === BLOCK_TYPES.DIVIDER);
  const isLast = activeStep === chunks.length - 1;
  const onBack = () => {
    setActiveStep(activeStep - 1);
  };

  const onNext = async () => {
    onSave && await onSave();
    if (!isLast) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };
  const activeBlocks = chunks[activeStep];

  if (!activeBlocks) return null;

  return (
    <div className={classes.root}>
      <Stepper
        alternativeLabel
        nonLinear
        classes={{
          root: classes.stepperRoot,
        }}
        activeStep={activeStep}
        connector={null}
      >
        {chunks.map((block, index) => (
          <Step
            key={block.id}
            classes={{
              alternativeLabel: classes.alternativeLabel,
            }}
          >
            <button
              className={cx(classes.stepButton, {
                active: index === activeStep,
              })}
              onClick={handleStep(index)}
            />
          </Step>
        ))}
      </Stepper>
      {activeBlocks.map((block) => (
        <Block
          key={block.id}
          onChange={onChange}
          block={block}
          index={blocks.indexOf(block)}
          classes={classes}
        />
      ))}
      {!hideActions && (
        <div className={classes.footer}>
          {activeStep > 0 && (
            <Button onClick={onBack} color="primary">
              Back
            </Button>
          )}
          <Box marginLeft="auto" display="flex" alignItems="center">
            <Button
              onClick={onNext}
              size="large"
              variant={isLast ? 'outlined' : 'contained'}
              color="primary"
            >
              {isLast ? 'Save' : 'Next'}
            </Button>
            {status === 'inprogress' && isLast && (
              <Button
                startIcon={<DoneAllIcon />}
                onClick={onSubmit}
                size="large"
                variant="contained"
                color="primary"
                style={{ marginLeft: 8 }}
              >
                Submit Exercise
              </Button>
            )}
            {status === 'done' && (
              <Button disabled variant="default" startIcon={<DoneAllIcon />}>
                Submitted
              </Button>
            )}
          </Box>
        </div>
      )}
    </div>
  );
};

export default ExerciseSteps;
